import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DataTable from '../Common/DataTable';
import DonutChart from '../Common/DonutChart';
import DashboardHero from '../Common/DashboardHero';
import { CURRENCY } from '../../config';

/* ═══════════════════════════════════════════════════════════════════
   SUBJECT COLOR FAMILIES
   ═══════════════════════════════════════════════════════════════════ */
const SUBJECT_FAMILIES = {
  'Mathematics':    ['#bfdbfe', '#60a5fa', '#1d4ed8'],
  'English':        ['#bbf7d0', '#4ade80', '#15803d'],
  'Science':        ['#fde68a', '#fbbf24', '#b45309'],
  'Social Studies': ['#ddd6fe', '#a78bfa', '#6d28d9'],
  'Computer':       ['#fbcfe8', '#f472b6', '#be185d'],
  'Urdu':           ['#a5f3fc', '#22d3ee', '#0e7490'],
  'Islamiat':       ['#d9f99d', '#a3e635', '#4d7c0f'],
  'Physics':        ['#c7d2fe', '#818cf8', '#3730a3'],
  'Chemistry':      ['#fecaca', '#f87171', '#b91c1c'],
  'Biology':        ['#fed7aa', '#fb923c', '#c2410c'],
};
const FALLBACK_FAMILY = ['#d1d5db', '#9ca3af', '#4b5563'];

const getSubjectFamily = (subject) => SUBJECT_FAMILIES[subject] || FALLBACK_FAMILY;

/* ═══════════════════════════════════════════════════════════════════
   Grouped bar chart — subject-colored
   ═══════════════════════════════════════════════════════════════════ */
function GroupedBarChart({ categories = [], series = [], height = 340, suffix = '%', title }) {
  if (!categories.length || !series.length) {
    return (
      <div className="bg-white p-3 sm:p-5 rounded shadow">
        {title && <h3 className="font-semibold mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">{title}</h3>}
        <p className="text-gray-400 text-center py-12">No data</p>
      </div>
    );
  }

  const allValues = series.flatMap(s => s.values.filter(v => v > 0));
  const maxValue = Math.max(...allValues, 1);
  const examShadeIndex = {};
  series.forEach((s, i) => { examShadeIndex[s.name] = i % 3; });
  const minChartWidth = Math.max(500, categories.length * 95);

  return (
    <div className="bg-white p-3 sm:p-5 rounded shadow overflow-hidden">
      {title && <h3 className="font-semibold mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">{title}</h3>}

      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-2 text-[11px] sm:text-xs">
        {categories.map(label => {
          const fullName =
            Object.keys(SUBJECT_FAMILIES).find(s =>
              s.startsWith(label.replace('…', '').slice(0, 6))
            ) || label;
          const family = getSubjectFamily(fullName);
          return (
            <div key={label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded flex-shrink-0" style={{ backgroundColor: family[1] }} />
              <span className="text-gray-700 font-medium whitespace-nowrap">{label}</span>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-gray-400 mb-3 sm:mb-4">
        Within each subject, shade intensifies by exam:{' '}
        {series.map((s, i) => (
          <span key={s.name}>
            {i > 0 && ' · '}
            <span className="font-medium text-gray-500">{s.name}</span>
          </span>
        ))}
      </p>

      <div className="overflow-x-auto -mx-1 px-1">
        <div style={{ minWidth: `${minChartWidth}px` }}>
          <div className="relative" style={{ height }}>
            <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-right pr-2">
              {[100, 75, 50, 25, 0].map(p => (
                <span key={p} className="text-[10px] text-gray-400">
                  {Math.round((p / 100) * maxValue)}{suffix}
                </span>
              ))}
            </div>
            <div className="ml-12 h-full flex flex-col">
              <div className="flex-1 relative flex items-end border-b border-gray-200">
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                  {[0, 25, 50, 75, 100].map(p => (
                    <div key={p} className="border-t border-gray-100 w-full" />
                  ))}
                </div>
                {categories.map((cat, catIdx) => {
                  const fullName =
                    Object.keys(SUBJECT_FAMILIES).find(s =>
                      s.startsWith(cat.replace('…', '').slice(0, 6))
                    ) || cat;
                  const family = getSubjectFamily(fullName);
                  return (
                    <div key={cat} className="flex-1 flex items-end justify-center gap-1 h-full relative z-10 px-1">
                      {series.map((s, sIdx) => {
                        const value = s.values[catIdx] || 0;
                        const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
                        const shadeIdx = examShadeIndex[s.name] ?? sIdx;
                        const color = value === 0 ? '#e5e7eb' : family[shadeIdx];
                        return (
                          <div key={sIdx} className="flex flex-col items-center justify-end h-full"
                            style={{ width: `${Math.min(90 / series.length, 45)}%` }}>
                            <span className="text-[10px] font-bold text-gray-700 mb-0.5 whitespace-nowrap">{value || ''}</span>
                            <div className="w-full rounded-t transition-all"
                              style={{
                                height: `${Math.max(pct, value === 0 ? 1 : 2)}%`,
                                backgroundColor: color,
                                minHeight: value === 0 ? '3px' : '5px',
                              }}
                              title={`${fullName} — ${s.name}: ${value}${suffix}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-around pt-2">
                {categories.map(cat => (
                  <div key={cat} className="flex-1 text-center">
                    <span className="text-[10px] sm:text-xs text-gray-600 font-medium block">{cat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {categories.length > 4 && (
        <p className="text-[10px] text-gray-400 text-center mt-2 sm:hidden">
          <i className="fas fa-arrows-alt-h mr-1"></i> Swipe to see all subjects
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Daily Attendance Grid
   ═══════════════════════════════════════════════════════════════════ */
function DailyAttendanceBars({ records = [], viewMonth }) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const recordMap = {};
  records.forEach(r => { recordMap[r.date] = r.status; });

  const days = [];
  const today = new Date();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dow = date.getDay();
    days.push({
      day: d, date: key,
      dayName: date.toLocaleDateString('en', { weekday: 'short' }),
      isWeekend: dow === 0 || dow === 6,
      isFuture: date > today,
      status: recordMap[key] || null,
    });
  }

  const cellClass = (day) => {
    if (day.isFuture) return 'bg-gray-50 border-gray-200 text-gray-300';
    if (day.status === 'Present') return 'bg-green-500 border-green-600 text-white';
    if (day.status === 'Absent')  return 'bg-red-500 border-red-600 text-white';
    if (day.status === 'Late')    return 'bg-yellow-400 border-yellow-500 text-white';
    if (day.isWeekend) return 'bg-gray-200 border-gray-300 text-gray-500';
    return 'bg-white border-gray-200 text-gray-400';
  };

  return (
    <div className="grid grid-cols-7 gap-1 max-w-md mx-auto">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
        <div key={d} className={`text-center text-[10px] font-bold uppercase pb-1 ${
          d === 'Sun' ? 'text-red-500' : d === 'Sat' ? 'text-orange-500' : 'text-gray-500'
        }`}>{d}</div>
      ))}
      {Array.from({ length: new Date(year, month, 1).getDay() }).map((_, i) => (
        <div key={`blank-${i}`} style={{ height: '32px' }} />
      ))}
      {days.map(day => (
        <div key={day.day}
          className={`rounded border-2 flex flex-col items-center justify-center text-[10px] font-semibold transition-all hover:scale-110 cursor-default ${cellClass(day)}`}
          style={{ height: '32px', minHeight: '32px' }}
          title={`${day.day} ${day.dayName} — ${day.status || (day.isFuture ? 'Upcoming' : day.isWeekend ? 'Weekend' : 'No record')}`}>
          <span className="text-xs font-bold leading-none">{day.day}</span>
          {day.status && !day.isFuture && (
            <span className="text-[7px] font-medium opacity-90 leading-none mt-0.5">{day.status[0]}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Class Timetable
   ═══════════════════════════════════════════════════════════════════ */
function ClassTimetable({ classId }) {
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId) { setTimetable([]); return; }
    setLoading(true);
    api.get(`/timetable/class/${classId}`)
      .then(res => setTimetable(res.data || []))
      .catch(() => setTimetable([]))
      .finally(() => setLoading(false));
  }, [classId]);

  if (loading) return <div className="bg-white p-3 sm:p-5 rounded shadow mb-6 text-center text-gray-400 py-12">Loading timetable...</div>;

  if (timetable.length === 0) {
    return (
      <div className="bg-white p-3 sm:p-5 rounded shadow mb-6">
        <h3 className="font-semibold text-gray-800 text-base sm:text-lg mb-4">🗓️ Class Timetable</h3>
        <div className="text-center py-8 text-gray-400">
          <i className="fas fa-calendar-times text-4xl mb-3"></i>
          <p>No timetable available yet.</p>
        </div>
      </div>
    );
  }

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const activeDays = DAYS.filter(d => timetable.some(t => t.day === d));
  const allPeriods = [...new Set(timetable.map(t => t.period))].sort((a, b) => a - b);
  const palette = [
    'bg-blue-100 text-blue-800 border-blue-300',
    'bg-green-100 text-green-800 border-green-300',
    'bg-yellow-100 text-yellow-800 border-yellow-300',
    'bg-purple-100 text-purple-800 border-purple-300',
    'bg-orange-100 text-orange-800 border-orange-300',
    'bg-pink-100 text-pink-800 border-pink-300',
    'bg-teal-100 text-teal-800 border-teal-300',
    'bg-indigo-100 text-indigo-800 border-indigo-300',
  ];
  const allSubjects = [...new Set(timetable.map(t => t.subject))];
  const subjectColors = {};
  allSubjects.forEach((s, i) => { subjectColors[s] = palette[i % palette.length]; });
  const getEntry = (day, period) => timetable.find(t => t.day === day && t.period === period);

  return (
    <div className="bg-white p-3 sm:p-5 rounded shadow mb-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-800 text-base sm:text-lg">🗓️ Class Timetable</h3>
        <span className="text-xs text-gray-500">{timetable.length} periods</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] border-collapse">
          <thead>
            <tr>
              <th className="p-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 w-28 border">Period</th>
              {activeDays.map(d => (
                <th key={d} className="p-2 bg-gray-100 text-center text-xs font-semibold text-gray-700 border">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allPeriods.map(period => {
              const sample = timetable.find(t => t.period === period);
              const timeRange = sample ? `${sample.start_time || ''}${sample.end_time ? '–' + sample.end_time : ''}` : '';
              return (
                <tr key={period}>
                  <td className="p-2 bg-gray-50 border">
                    <div className="text-xs font-semibold text-gray-700">Period {period}</div>
                    {timeRange && <div className="text-[10px] text-gray-400">{timeRange}</div>}
                  </td>
                  {activeDays.map(day => {
                    const entry = getEntry(day, period);
                    return (
                      <td key={day} className="p-1 border text-center">
                        {entry ? (
                          <div className={`p-1.5 rounded text-center text-[11px] font-medium border ${subjectColors[entry.subject] || 'bg-gray-100 text-gray-800 border-gray-300'}`}>
                            <div>{entry.subject}</div>
                            {entry.teacher_name && <div className="text-[9px] text-gray-600 mt-0.5 truncate">{entry.teacher_name}</div>}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   Order:
     1. Hero banner
     2. Summary stats
     3. Select a Child
     4. Children Overview
     5. 📚 Their Own Grades (subject-wise, first!)
     6. 🎯 Average Grade + Quick Summary (second)
     7. Fee Breakdown
     8. Attendance (daily/monthly)
     9. Class Timetable
   ═══════════════════════════════════════════════════════════════════ */
export default function ParentDashboard() {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceView, setAttendanceView] = useState('daily');
  const [attendanceMonth, setAttendanceMonth] = useState(new Date());
  const [selectedExams, setSelectedExams] = useState([]);

  useEffect(() => { fetchChildren(); }, []);

  useEffect(() => {
    setAttendanceMonth(new Date());
    setSelectedExams([]);
  }, [selectedChild?.student_id]);

  const fetchChildren = async () => {
    setLoading(true);
    try {
      const res = await api.get('/parent-students/my-children');
      setChildren(res.data);
      if (res.data.length > 0) setSelectedChild(res.data[0]);
    } catch (err) {
      console.error('Failed to load children:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading...</div>;

  if (children.length === 0) {
    return (
      <div className="bg-white p-8 rounded shadow text-center">
        <i className="fas fa-inbox text-4xl text-gray-300 mb-3"></i>
        <p className="text-gray-500">No children linked to your account.</p>
      </div>
    );
  }

  const totalChildren = children.length;
  const avgAttendance = Math.round(children.reduce((s, c) => s + (c.attendance_percent || 0), 0) / totalChildren);
  const avgGrade = Math.round(children.reduce((s, c) => s + (c.average_percent || 0), 0) / totalChildren);
  const totalPending = children.reduce((s, c) => s + (c.pending_fees || 0), 0);
  const allPaid = totalPending <= 0;
  const child = selectedChild;

  const allGrades = child?.grades_by_subject || [];
  const allExams = [...new Set(allGrades.map(g => g.exam).filter(Boolean))].sort();
  const subjects = [...new Set(allGrades.map(g => g.subject))].sort();
  const examColors = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6'];
  const examsToShow = selectedExams.length === 0 ? allExams : selectedExams;

  const toggleExam = (exam) => {
    setSelectedExams(prev =>
      prev.includes(exam) ? prev.filter(e => e !== exam) : [...prev, exam]
    );
  };

  const gradeSeries = examsToShow.map((exam, i) => ({
    name: exam,
    color: examColors[i % examColors.length],
    values: subjects.map(subj => {
      const g = allGrades.find(x => x.subject === subj && x.exam === exam);
      return g ? g.percentage : 0;
    }),
  }));

  const subjectLabels = subjects.map(s => s.length > 8 ? s.substring(0, 7) + '…' : s);

  const buildMonthlyData = () => {
    const records = child?.attendance_records || [];
    const byMonth = {};
    records.forEach(r => {
      const m = r.date.substring(0, 7);
      if (!byMonth[m]) byMonth[m] = { present: 0, total: 0 };
      byMonth[m].total++;
      if (r.status === 'Present') byMonth[m].present++;
    });
    return Object.keys(byMonth).sort().map(m => {
      const d = byMonth[m];
      const pct = d.total ? Math.round((d.present / d.total) * 100) : 0;
      return {
        label: new Date(m + '-01').toLocaleDateString('en', { month: 'short', year: '2-digit' }),
        value: pct,
        color: pct >= 90 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#ef4444',
      };
    });
  };
  const monthlyChartData = buildMonthlyData();

  const feeDonutData = [
    { label: 'Paid',    value: child?.paid_fees || 0,    color: '#10b981' },
    { label: 'Pending', value: child?.pending_fees || 0, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const monthLabel = attendanceMonth.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  const isCurrentMonth =
    attendanceMonth.getMonth() === new Date().getMonth() &&
    attendanceMonth.getFullYear() === new Date().getFullYear();

  const goPrevMonth = () => {
    const d = new Date(attendanceMonth);
    d.setMonth(d.getMonth() - 1);
    setAttendanceMonth(d);
  };
  const goNextMonth = () => {
    const d = new Date(attendanceMonth);
    d.setMonth(d.getMonth() + 1);
    setAttendanceMonth(d);
  };
  const goToday = () => setAttendanceMonth(new Date());

  const columns = ['Child', 'Class', 'Campus', 'Class Avg Grade', 'Attendance Rate', 'Fee Status'];
  const data = children.map(c => ({
    Child: (
      <span
        className={`font-medium cursor-pointer hover:underline ${
          selectedChild?.student_id === c.student_id ? 'text-blue-700 font-bold' : 'text-blue-600'
        }`}
        onClick={() => setSelectedChild(c)}
      >
        {selectedChild?.student_id === c.student_id && '▶ '}
        {c.name}
      </span>
    ),
    Class: c.class_name,
    Campus: c.campus_name,
    'Class Avg Grade': c.average_percent ? `${c.average_percent}%` : '—',
    'Attendance Rate': (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        c.attendance_percent >= 90 ? 'bg-green-100 text-green-800'
        : c.attendance_percent >= 75 ? 'bg-yellow-100 text-yellow-800'
        : 'bg-red-100 text-red-800'
      }`}>{c.attendance_percent}%</span>
    ),
    'Fee Status': (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        c.fees_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {c.fees_paid ? 'Paid' : `Pending ${CURRENCY}${c.pending_fees.toFixed(0)}`}
      </span>
    ),
  }));

  return (
    <div>
      {/* ═══ HERO ═══ */}
      <DashboardHero
        color="amber"
        icon="fa-user-friends"
        title="Parent Dashboard"
        subtitle={`${totalChildren} ${totalChildren === 1 ? 'child' : 'children'} · Avg Attendance ${avgAttendance}% · Avg Grade ${avgGrade}%`}
      />

      {/* ═══ 1. SUMMARY ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
        <StatsCard label="My Children" value={totalChildren} icon="fa-child" />
        <StatsCard label="Avg Grade" value={`${avgGrade}%`} icon="fa-chart-line" />
        <StatsCard label="Avg Attendance" value={`${avgAttendance}%`} icon="fa-percent" />
        <StatsCard
          label={allPaid ? 'All Fees Paid' : 'Pending Fees'}
          value={allPaid ? '✓' : `${CURRENCY}${totalPending.toFixed(0)}`}
          icon={allPaid ? 'fa-check-circle' : 'fa-exclamation-circle'}
        />
      </div>

      {/* ═══ 2. CHILD SELECTOR ═══ */}
      <div className="bg-white p-3 sm:p-5 rounded shadow mb-4">
        <h3 className="font-semibold mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">
          👨‍👩‍👧‍👦 Select a Child
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {children.map(c => (
            <button
              key={c.student_id}
              onClick={() => setSelectedChild(c)}
              className={`p-3 sm:p-4 rounded-lg border-2 text-left transition ${
                selectedChild?.student_id === c.student_id
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-base sm:text-lg flex-shrink-0 ${
                  selectedChild?.student_id === c.student_id
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {c.first_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {c.class_name} · {c.campus_name}
                    {c.roll_no && ` · Roll #${c.roll_no}`}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ═══ 3. CHILDREN OVERVIEW ═══ */}
      <div className="bg-white p-3 sm:p-4 rounded shadow mb-4">
        <h2 className="text-base sm:text-lg font-semibold mb-2">📋 Children Overview</h2>
        <p className="text-xs text-gray-500 mb-3">
          <i className="fas fa-info-circle"></i> Click a child's name to switch
        </p>
        <DataTable columns={columns} data={data} />
      </div>

      {/* ═══ PERFORMANCE HEADER (which child) ═══ */}
      <div className="mb-4 mt-2">
        <h2 className="text-lg sm:text-xl font-bold mb-1 text-gray-800">
          📊 {child?.name}'s Performance — {child?.class_name}
        </h2>
        {child?.registration_number && (
          <p className="text-xs text-gray-500 font-mono">
            Registration: {child.registration_number}
            {child.roll_no && ` · Roll #${child.roll_no}`}
          </p>
        )}
      </div>

      {/* ═══ 5. 📚 THEIR OWN GRADES (SUBJECT-WISE) — FIRST ═══ */}
      <div className="mb-6">
        {/* Exam filter */}
        <div className="bg-white p-3 rounded shadow mb-3">
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              <i className="fas fa-filter text-blue-500 mr-1"></i> Filter Exams:
            </label>
            {selectedExams.length > 0 && (
              <button onClick={() => setSelectedExams([])} className="text-xs text-blue-600 hover:underline ml-auto">Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedExams([])}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border ${
                selectedExams.length === 0
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}>
              <i className="fas fa-list mr-1"></i> All
            </button>
            {allExams.map((exam, i) => {
              const isActive = selectedExams.includes(exam);
              const color = examColors[i % examColors.length];
              return (
                <button key={exam} onClick={() => toggleExam(exam)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border ${
                    isActive ? 'text-white shadow-sm border-transparent' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                  }`}
                  style={isActive ? { backgroundColor: color } : {}}>
                  {isActive && <i className="fas fa-check mr-1"></i>}
                  {exam}
                </button>
              );
            })}
          </div>
        </div>

        {/* Subject-wise chart — the child's OWN grades */}
        <GroupedBarChart
          title={`📚 ${child?.first_name}'s Own Grades by Subject`}
          categories={subjectLabels}
          series={gradeSeries}
          height={340}
          suffix="%"
        />
      </div>

      {/* ═══ 6. 🎯 AVERAGE GRADE + QUICK SUMMARY — SECOND ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        {/* Average grade big card */}
        <div className="bg-white p-3 sm:p-5 rounded shadow">
          <h3 className="font-semibold mb-4 text-gray-800 text-base sm:text-lg">
            🎯 {child?.first_name}'s Average Grade
          </h3>

          {/* Big number */}
          <div className="bg-blue-50 rounded-lg p-6 text-center mb-4">
            <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide mb-1">
              Overall Average
            </p>
            <p className="text-5xl font-bold text-blue-900">
              {child?.average_percent ?? 0}%
            </p>
            <p className="text-[11px] text-blue-600 mt-2">
              Across all subjects & exams
            </p>
          </div>

          {/* Letter grade badge */}
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3">
            <span className="text-sm text-blue-700 font-medium">Letter Grade</span>
            <span className={`text-2xl font-bold ${
              (child?.average_percent ?? 0) >= 80 ? 'text-green-700'
              : (child?.average_percent ?? 0) >= 60 ? 'text-blue-700'
              : 'text-yellow-700'
            }`}>
              {(child?.average_percent ?? 0) >= 90 ? 'A+'
                : (child?.average_percent ?? 0) >= 85 ? 'A'
                : (child?.average_percent ?? 0) >= 80 ? 'A-'
                : (child?.average_percent ?? 0) >= 75 ? 'B+'
                : (child?.average_percent ?? 0) >= 70 ? 'B'
                : (child?.average_percent ?? 0) >= 65 ? 'B-'
                : (child?.average_percent ?? 0) >= 60 ? 'C'
                : (child?.average_percent ?? 0) >= 50 ? 'D'
                : 'F'}
            </span>
          </div>
        </div>

        {/* Quick summary */}
        <div className="bg-white p-3 sm:p-5 rounded shadow">
          <h3 className="font-semibold mb-4 text-gray-800 text-base sm:text-lg">
            📋 {child?.first_name}'s Quick Summary
          </h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded gap-2">
              <span className="text-blue-700 font-medium text-sm sm:text-base">Average Grade</span>
              <span className="text-xl sm:text-2xl font-bold text-blue-900">{child?.average_percent}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded gap-2">
              <span className="text-green-700 font-medium text-sm sm:text-base">Attendance</span>
              <span className="text-xl sm:text-2xl font-bold text-green-900">{child?.attendance_percent}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded gap-2">
              <span className="text-yellow-700 font-medium text-sm sm:text-base">Fees Paid</span>
              <span className="text-xl sm:text-2xl font-bold text-yellow-900">{CURRENCY}{child?.paid_fees?.toFixed(0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded gap-2">
              <span className="text-red-700 font-medium text-sm sm:text-base">Fees Pending</span>
              <span className="text-xl sm:text-2xl font-bold text-red-900">{CURRENCY}{child?.pending_fees?.toFixed(0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 7. FEE BREAKDOWN + ATTENDANCE ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        <DonutChart
          title={`💰 ${child?.first_name}'s Fee Breakdown`}
          data={feeDonutData}
          size={260}
        />

        <div className="bg-white p-3 sm:p-5 rounded shadow">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
              📅 {child?.first_name}'s Attendance
            </h3>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button onClick={() => setAttendanceView('daily')}
                className={`px-3 py-1 text-xs font-semibold transition ${attendanceView === 'daily' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}>
                <i className="fas fa-calendar-day mr-1"></i> Daily
              </button>
              <button onClick={() => setAttendanceView('monthly')}
                className={`px-3 py-1 text-xs font-semibold transition border-l ${attendanceView === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}>
                <i className="fas fa-calendar-alt mr-1"></i> Monthly
              </button>
            </div>
          </div>

          {attendanceView === 'daily' ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <button onClick={goPrevMonth} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"><i className="fas fa-chevron-left"></i></button>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-800">
                    <i className="fas fa-calendar-alt text-blue-500 mr-1"></i>{monthLabel}
                  </p>
                  {!isCurrentMonth && <button onClick={goToday} className="text-[10px] text-blue-600 hover:underline mt-0.5">Jump to Today</button>}
                </div>
                <button onClick={goNextMonth} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"><i className="fas fa-chevron-right"></i></button>
              </div>

              <DailyAttendanceBars records={child?.attendance_records || []} viewMonth={attendanceMonth} />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-green-50 rounded p-2"><p className="text-[10px] text-green-600 font-semibold">Present</p><p className="text-base font-bold text-green-700">{child?.attendance_present || 0}</p></div>
                <div className="bg-yellow-50 rounded p-2"><p className="text-[10px] text-yellow-600 font-semibold">Late</p><p className="text-base font-bold text-yellow-700">{child?.attendance_late || 0}</p></div>
                <div className="bg-red-50 rounded p-2"><p className="text-[10px] text-red-600 font-semibold">Absent</p><p className="text-base font-bold text-red-700">{child?.attendance_absent || 0}</p></div>
                <div className="bg-blue-50 rounded p-2"><p className="text-[10px] text-blue-600 font-semibold">Rate</p><p className="text-base font-bold text-blue-700">{child?.attendance_percent || 0}%</p></div>
              </div>
            </>
          ) : (
            monthlyChartData.length === 0 ? (
              <p className="text-gray-400 text-center py-12">No monthly data yet.</p>
            ) : (
              <GroupedBarChart
                title=""
                categories={monthlyChartData.map(d => d.label)}
                series={[{ name: 'Attendance %', color: '#3b82f6', values: monthlyChartData.map(d => d.value) }]}
                height={280}
                suffix="%"
              />
            )
          )}
        </div>
      </div>

      {/* ═══ 8. CLASS TIMETABLE ═══ */}
      <ClassTimetable classId={child?.class_id} />
    </div>
  );
}